<template>
    <div class="box">
        <Modal
                v-model="dialog"
                :title="$t('realEditor.List.editDialog.title')"
                :mask-closable="false"
                @keydown.native.enter.prevent ="debounce(save)"
        >
            <Form :label-width="100" :rules="realEditorRules" ref="realEditorForm" :model="realEditorSettings">
                <FormItem label="名称" style="margin-bottom: 0" prop="name">
                    <Input v-model="realEditorSettings.name" show-word-limit :maxlength="50"/>
                </FormItem>
            </Form>
            <div slot="footer">
                <Button type="text" size="large" @click="dialog=false">{{ $t('Button.cancel') }}</Button>
                <Button type="primary" size="large" :loading="save_loading" @click="debounce(save)">{{ $t('Button.confirm') }}</Button>
            </div>
        </Modal>
        <div class="box">
            <div class="header">
                <Row v-if="hasPermission('addRealEditor')" type="flex" class="toolbar">
                    <Col span="24">
                    <Button icon="md-add" type="primary" @click="create">{{ $t('realEditor.List.button.add') }}</Button>
                    </Col>
                </Row>
                <div class="filter">
                    <ul class="filter-box">
                        <li>
                            <span>{{ $t('realEditor.List.filter.titleLike') }}</span>
                            <Input
                                    clearable
                                    v-model="filter.nameLike"
                                    size="small"
                                    class="selector"
                                    :placeholder="$t('Message.placeholder')"
                            />
                        </li>
                        <li v-if="dep||(filter.selectType==3)">
                            <span>{{ $t('realEditor.List.filter.userCode') }}</span>
                            <Input
                                    clearable
                                    v-model="filter.userCode"
                                    size="small"
                                    class="selector"
                                    :placeholder="$t('Message.placeholder')"
                            />
                        </li>
                        <li>
                            <span>{{ $t('realEditor.List.filter.status') }}</span>
                            <Select v-model="filter.status" size="small" class="selector" clearable transfer :placeholder="$t('Message.placeholderSelect')">
                                <Option :value="2">{{ $t('realEditor.List.status.running') }}</Option>
                                <Option :value="6">{{ $t('realEditor.List.status.close') }}</Option>
                            </Select>
                        </li>
                        <li>
                            <DepartmentSelect filterPath="views/realEditor.all.filter"></DepartmentSelect>
                        </li>
                        <li v-if="filter.selectType == 3">
                            <UserGroupSelect v-model="groupIdSelected.id" ></UserGroupSelect>
                        </li>
                    </ul>
                    <Button 
                        class="refresh-button" 
                        type="primary" 
                        size="small"
                        @click="refresh">
                        {{ $t('Button.refresh') }}
                    </Button>
                </div>
            </div>
            <div ref="list" class="content">
                <table class="list">
                    <thead>
                    <!--<td width="50">序号</td>-->
                    <td>{{ $t('realEditor.List.list.id') }}</td>
                    <td>{{ $t('realEditor.List.list.name') }}</td>
                    <td>{{ $t('realEditor.List.list.status') }}</td>
                    <td>{{ $t('realEditor.List.list.createDatetime') }}</td>
                    <td v-if="dep||(filter.selectType==3)">{{ $t('realEditor.List.list.userName') }}</td>
                    <td :width="isCordova()?50:350">{{ $t('realEditor.List.list.operation') }}</td>
                    </thead>

                    <tr v-for="i in list" :key="i.id">
                        <!--<td></td>-->
                        <td>{{i.id}}</td>
                        <td>{{i.name}}</td>
                        <td v-if="i.status == 0">{{ $t('realEditor.List.status.init') }}</td>
                        <td v-else-if="i.status == 2">{{ $t('realEditor.List.status.running') }}</td>
                        <td v-else-if="i.status == 5">{{ $t('realEditor.List.status.error') }}</td>
                        <td v-else-if="i.status == 6">{{ $t('realEditor.List.status.close') }}</td>
                        <td v-else-if="i.status == 7">{{ $t('realEditor.List.status.pastDue') }}</td>
                        <td v-else>{{ $t('realEditor.List.status.stop') }}</td>
                        <td>{{ i.createDatetime }}</td>
                        <td v-if="dep||(filter.selectType==3)">{{ i.userName }}</td>
                        <td>
                            <Button
                                    v-if="hasPermission('realEditorConsole')"
                                    type="dashed"
                                    size="large"
                                    icon="md-calculator"
                                    @click="controlPanel(i.id, i.userId)"
                            >{{ $t('realEditor.List.button.controlPanel') }}</Button>
                            <Button
                                    v-if="hasPermission('editRealEditor')"
                                    type="dashed"
                                    size="large"
                                    icon="md-settings"
                                    @click="edit(i.id,i.name)"
                            >{{ $t('Button.edit') }}</Button>
                            <Button
                                    v-if="hasPermission('deleteRealEditor')&&(i.userId==userId)"
                                    type="dashed"
                                    size="large"
                                    icon="md-close"
                                    @click="remove(i.id,i.name)"
                            >{{ $t('Button.remove') }}</Button>
                        </td>
                    </tr>
                </table>

                <Spin v-show="loading" fix class="spin">
                    <Icon type="ios-loading" size="18" class="spin-icon-load"></Icon>
                    <div>Loading</div>
                </Spin>
                <Page
                        class="pager"
                        :total="pagination.total"
                        :current="pagination.page"
                        :page-size="pagination.pageSize"
                        @on-change="v=>pagination.page=v"
                        @on-page-size-change="v=>pagination.pageSize=v"
                        show-elevator
                        show-sizer
                        transfer
                />
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapMutations } from "vuex";
    import api from "@/api/Api";
    import extend from "extend";
    import config from "@/config"
    import DepartmentSelect from "../../../components/DepartmentSelect"
    import UserGroupSelect from "../../../components/UserGroupSelect"
    import HtmlDecode from "@/plugins/htmlDecode";
    export default {
        components:{
            DepartmentSelect,
            UserGroupSelect
        },
        data(){
            return {
                groupIdSelected: {
                    id: this.$store.state.user.defaultSelectedUserGroup,
                    groupData: []
                },
                loading: true,
                dialog: false,
                save_loading: false,
                list: [],
                realEditorSettings: {
                    id: 0,
                    name: ''
                },
                realEditorRules: {
                    name: [
                        { required: true, message: '名称不能为空', trigger: 'blur',
                            validator: (rule, value, callback) => {
                                let tmp = value.trim();
                                if (tmp.length < 1) {
                                    callback(new Error('名称不能为空'));
                                } else if(tmp.length > 50) {
                                    callback(new Error('名称不能超过50个字'));
                                } else {
                                    callback();
                                }
                            }
                        }
                    ]
                }
            }
        },
        computed: {
            filter: {
                get() {
                    return this.$store.state.views.realEditor.all.filter;
                },
                set(v) {
                    this.$store.commit("views/realEditor.all.filter", v);
                }
            },
            pagination: {
                get() {
                    return this.$store.state.views.realEditor.all.pagination;
                },
                set(v) {
                    this.$store.commit("views/realEditor.all.pagination", v);
                }
            },
            ...mapState({
                globalConfig: state => state.user.globalConfig,
                shareId: state => state.user.defaultSelectedUserGroup,
                userId: state => state.user.id,
            }),
            dep(){
                return this.filter.selectType == 2
            }
        },
        watch: {
            'groupIdSelected.id'(){
                this.pagination.page = 1;
                this.refresh(); 
            },
            'filter.depId'() {
                this.pagination.page = 1;
                this.refresh();
            },
            'filter.nameLike'() {
                if(this.containsInvaildChar(this.filter.nameLike)) {
                    return;
                }
                this.pagination.page = 1;
                this.refresh();
            },
            'filter.userCode'() {
                this.pagination.page = 1;
                this.refresh();
            },
            'filter.status'() {
                this.pagination.page = 1;
                this.refresh();
            },
            "pagination.page": "refresh",
            "pagination.pageSize": "refresh"
        },
        methods: {
            controlPanel(id, mid){
                let url = config.realTimeEditUrl
                        .replace("{{id}}", id)
                        + "&mId=" + mid
                        + "&mediaPublishFlag=" + this.globalConfig.mediaPublishFlag
                        + "&selectType=" + this.filter.selectType;
                window.open(url, "_blank")
            },
            edit(id, name){
                this.dialog = true;
                this.realEditorSettings.id = id;
                this.realEditorSettings.name = name;
            },
            create(){
                this.dialog = true;
                this.realEditorSettings.id = 0;
                this.realEditorSettings.name = "";
            },
            async save(){
                if(this.hasPermission('saveRealEditor')){
                    this.save_loading = true;
                    this.realEditorSettings.name = this.realEditorSettings.name.trim();
                    if(this.realEditorSettings.name == ""){
                        this.$Message.error(this.$t('Message.save.nameEmpty'))
                    }else {
                        try {
                            let ret = await api.call("realEditor/save", this.realEditorSettings);
                            this.$Notice.success({ title: this.$t('Message.save.success') });
                            this.dialog = false;
                            this.refresh();
                        } catch (e) {
                            this.$Notice.error({ title: this.$t('Message.save.error'), desc: e.resultMsg });
                        }
                    }
                    setTimeout(()=>{
                        this.save_loading = false;
                    },2000);
                }else {
                    this.$Notice.error({ title: this.$t('Message.permission.error') });
                }
            },
            async remove(id, title) {
                if (!confirm(this.$t('Message.remove.confirm') + `(${title}) ?`)) return;

                this.loading = true;

                try {
                    let ret = await api.call("realEditor/delete", {
                        id: id
                    });

                    this.$Notice.success({ title: this.$t('Message.remove.success') + `(${title})` });

                    this.refresh();
                } catch (e) {
                    this.$Notice.error({ title: this.$t('Message.remove.error') + `(${title})`, desc: e.resultMsg });
                }

                this.loading = false;
            },
            async refresh() {
                this.loading = true;

                try {
                    let param = extend(true, {}, this.pagination, this.filter)
                    if(this.filter.selectType == 3){
                        param.shareId = this.shareId
                    }
                    console.log(param);
                    
                    let ret = await api.call(
                        "realEditor/list",
                        param
                    );
                    if(ret.list) {
                        ret.list.forEach(i => {
                            i.name = HtmlDecode.d(i.name);
                        });
                    }
                    this.list = ret.list;
                    this.pagination.total = ret.total;
                } catch (e) {
					this.$Notice.error({
						title: this.$t(e && e.reject ?
							'Message.permission.error' :
							'Message.callResult.retry')
					});
					this.list = [];
                    this.pagination.total = 0 ;
                }

                this.loading = false;
            },
            //更新状态
            async refreshStatus() {
                if(this.hasPermission('refreshRealEditor')){
                    try {
                        let ids = this.list.map(i => i.id);

                        if (!ids.length) return;

                        let ret = await api.call("realEditor/refreshData", { ids });

                        let needRefresh = false;
                        this.list = this.list.map(i => {
                            //在返回的列表里面搜索对应的 id
                            let found = ret.list.filter(j => j.id == i.id)[0];
                            if (found) {
                                if (
                                    (i.status == 0 &&
                                        found.status == 2) ||
                                    (i.status != 6 &&
                                        found.status == 6)
                                ) {
                                    needRefresh = true;
                                }

                                //更新 status
                                i.status = found.status;
                                i.finishRate = found.finishRate;
                            }
                            return i;
                        });
                        needRefresh && this.refresh(true);
                    } catch (e) {}
                }
            }
        },
        mounted() {
            this.refresh();
            this._statusTmr = setInterval(this.refreshStatus, 2000);
        },
        created() {},
        beforeDestroy() {
            clearInterval(this._statusTmr);
        }
    }
</script>

<style lang="less" scoped>
    @keyframes spin1 {
        from {
            transform: rotate(0deg);
        }
        50% {
            transform: rotate(180deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
    .box {
        min-height:400px;
    }
    .header {
        & > * {
            margin-bottom: 10px;
            border-radius: 5px;
        }
        .toolbar {
            height: 32px;
            line-height: 32px;

            button {
                margin-right: 22px;
                vertical-align: middle;
            }

            .search {
                width: 200px;
            }
        }
        .filter {
            padding: 10px 20px;
            background: #fff;

            .filter-box {
                list-style: none;
                display: inline-block;
                & > li {
                    display: inline-block;
                }
            }
        }
    }
    .content {
        position: relative;
        border-radius: 5px;
        background: white;
        padding: 0 20px;

        .spin {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
        }
    }
    .list {
        width: 100%;
        border-radius: 5px;
        border-collapse: collapse; /*表格的边框合并，如果相邻，则共用一个边框*/
        border-spacing: 0; /*设置行与单元格边框的间距。当表格边框独立（即border-collapse:separate;）此属性才起作用*/

        td {
            padding: 12px 0;
            border-bottom: 1px solid #e9e9e8;
        }
    }
    .pager {
        text-align: center;
        padding: 10px;
    }

    .selector {
        display: inline-block;
        width: auto;
    }
</style>